import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useThemeMode } from './ThemeContext';
import { RecordingProvider, useRecording } from './RecordingContext';
import HomePage from './pages/HomePage';
import UploadPage from './pages/UploadPage';
import ReviewPage from './pages/ReviewPage';
import DashboardPage from './pages/DashboardPage';
import TrendsPage from './pages/TrendsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import HistoryPage from './pages/HistoryPage';
import WorkoutLogPage from './pages/WorkoutLogPage';
import WorkoutHistoryPage from './pages/WorkoutHistoryPage';
import InvestmentLogPage from './pages/InvestmentLogPage';
import InvestmentHoldingsPage from './pages/InvestmentHoldingsPage';
import InvestmentLedgerPage from './pages/InvestmentLedgerPage';
import CareerMapperPage from './pages/CareerMapperPage';
import CareerMapperViewPage from './pages/CareerMapperViewPage';
import CareerComparisonPage from './pages/CareerComparisonPage';
import InterviewPrepPage from './pages/InterviewPrepPage';
import InterviewUploadPage from './pages/InterviewUploadPage';
import InterviewReviewPage from './pages/InterviewReviewPage';
import ChatPage from './pages/ChatPage';
import JournalPage from './pages/JournalPage';
import TrackerPage from './pages/TrackerPage';
import RehearsalsPage from './pages/RehearsalsPage';

const NAV_LINKS = {
  expense: [
    { label: 'Upload', to: '/expense/upload' },
    { label: 'History', to: '/expense/history' },
    { label: 'Dashboard', to: '/expense/dashboard' },
    { label: 'Trends', to: '/expense/trends' },
    { label: 'Analytics', to: '/expense/analytics' },
  ],
  workout: [
    { label: 'Log Workout', to: '/workout/log' },
    { label: 'History', to: '/workout/history' },
  ],
  investments: [
    { label: 'Holdings', to: '/investments/holdings' },
    { label: 'Ledger', to: '/investments/ledger' },
    { label: 'Log Transaction', to: '/investments/log' },
  ],
  career: [
    { label: 'Profiles', to: '/career' },
    { label: 'Compare', to: '/career/compare' },
  ],
  interview: [
    { label: 'Stories Bank', to: '/interview' },
    { label: 'Upload / Extract', to: '/interview/upload' },
  ],
  chat: [],
  journal: [],
  tracker: [],
  rehearsals: [],
};

const MODULE_LABELS = {
  expense: 'Expense Tracker',
  workout: 'Workout Tracker',
  investments: 'Investments',
  career: 'Career Mapper',
  interview: 'Interview Prep',
  chat: 'AI Chat',
  journal: 'Journal',
  tracker: 'Tracker',
  rehearsals: 'Rehearsals',
};

function NavLink({ to, label }) {
  const location = useLocation();
  const active = location.pathname === to;
  return (
    <Button
      component={Link}
      to={to}
      size="small"
      sx={{
        color: active ? '#fff' : 'rgba(255,255,255,0.6)',
        fontWeight: active ? 700 : 500,
        fontSize: '0.8125rem',
        px: 1.5,
        borderRadius: 1.5,
        backgroundColor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
        '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
      }}
    >
      {label}
    </Button>
  );
}

function NotificationBell() {
  const { notifications, markAllRead } = useRecording();
  const [anchor, setAnchor] = useState(null);
  const navigateTo = useNavigate();
  const unread = notifications.filter(n => !n.read).length;

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title="Notifications">
        <IconButton size="small"
          onClick={e => { setAnchor(e.currentTarget); markAllRead(); }}
          sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}
        >
          {unread > 0
            ? <NotificationsIcon fontSize="small" sx={{ color: '#fbbf24' }} />
            : <NotificationsNoneIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      {unread > 0 && (
        <Box sx={{ position: 'absolute', top: 2, right: 2, width: 14, height: 14, borderRadius: '50%', bgcolor: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Typography sx={{ fontSize: '0.5rem', color: '#fff', fontWeight: 700 }}>{unread}</Typography>
        </Box>
      )}
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', borderRadius: 2, width: 300, maxHeight: 380, overflowY: 'auto' } }}
      >
        <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid var(--bd-lo)' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--tx-hi)' }}>Notifications</Typography>
        </Box>
        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ color: 'var(--tx-gh)', fontSize: '0.78rem' }}>No notifications yet</Typography>
          </Box>
        ) : notifications.map(n => (
          <Box key={n.id}
            onClick={() => {
              setAnchor(null);
              if (n.type === 'done' && n.journal_id) {
                // Navigate to journal filtered to the linked activity
                if (n.tracker_node_id) {
                  navigateTo(`/journal?nodeId=${encodeURIComponent(n.tracker_node_id)}&nodeTitle=${encodeURIComponent(n.tracker_node_title || '')}`);
                } else {
                  navigateTo('/journal');
                }
              }
            }}
            sx={{ px: 2, py: 1.25, borderBottom: '1px solid var(--bg-low)', cursor: n.type === 'done' ? 'pointer' : 'default', '&:hover': n.type === 'done' ? { bgcolor: 'var(--bg-hover)' } : {} }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              {n.type === 'done'
                ? <CheckCircleIcon sx={{ fontSize: 15, color: '#10b981', mt: 0.2, flexShrink: 0 }} />
                : n.type === 'failed'
                  ? <ErrorIcon sx={{ fontSize: 15, color: '#ef4444', mt: 0.2, flexShrink: 0 }} />
                  : <HourglassEmptyIcon sx={{ fontSize: 15, color: '#f59e0b', mt: 0.2, flexShrink: 0 }} />}
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--tx-md)' }}>{n.title}</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'var(--tx-lo)', mt: 0.2 }}>{n.body}</Typography>
                {n.tracker_node_title && (
                  <Typography sx={{ fontSize: '0.65rem', color: '#818cf8', mt: 0.2 }}>{n.tracker_node_title}</Typography>
                )}
                {n.type === 'done' && (
                  <Typography sx={{ fontSize: '0.65rem', color: '#6366f1', mt: 0.3, fontWeight: 600 }}>Open in Journal →</Typography>
                )}
              </Box>
            </Stack>
          </Box>
        ))}
      </Popover>
    </Box>
  );
}

function Navigation() {
  const location = useLocation();
  const module = Object.keys(NAV_LINKS).find(m => location.pathname.startsWith(`/${m}`));
  const isHome = location.pathname === '/';
  const { isDark, toggleTheme } = useThemeMode();
  const isMobile = useMediaQuery('(max-width:640px)');
  const [navAnchor, setNavAnchor] = useState(null);

  const navLinks = (!isHome && module) ? NAV_LINKS[module] : [];
  const hasLinks = navLinks.length > 0;

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      window.location.href = '/cdn-cgi/access/logout';
    }
  };

  return (
    <AppBar position="sticky" elevation={0}>
      <Toolbar sx={{ minHeight: '52px !important', px: { xs: 2, md: 3 }, gap: { xs: 1, md: 2 } }}>
        {/* Brand */}
        <Typography
          component={Link}
          to="/"
          variant="h6"
          sx={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--tx-hi)', textDecoration: 'none', letterSpacing: '-0.02em', flexShrink: 0 }}
        >
          LifeHub
        </Typography>

        {/* Module badge */}
        {module && (
          <Chip label={MODULE_LABELS[module]} size="small"
            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: 'rgba(99,102,241,0.3)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.4)', flexShrink: 0 }}
          />
        )}

        {/* Nav links — desktop */}
        {hasLinks && !isMobile && (
          <Box sx={{ display: 'flex', gap: 0.5, flex: 1, flexWrap: 'wrap' }}>
            {navLinks.map(link => <NavLink key={link.to} {...link} />)}
          </Box>
        )}

        {/* Nav links — mobile hamburger */}
        {hasLinks && isMobile && (
          <>
            <IconButton size="small" onClick={e => setNavAnchor(e.currentTarget)}
              sx={{ color: 'rgba(255,255,255,0.8)', flexShrink: 0 }}>
              <MenuIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={navAnchor} open={Boolean(navAnchor)} onClose={() => setNavAnchor(null)}
              PaperProps={{ sx: { bgcolor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, minWidth: 180, mt: 0.5 } }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              {navLinks.map(link => (
                <MenuItem key={link.to} component={Link} to={link.to}
                  selected={location.pathname === link.to}
                  onClick={() => setNavAnchor(null)}
                  sx={{ fontSize: '0.85rem', py: 1.2,
                    color: location.pathname === link.to ? '#a5b4fc' : 'rgba(255,255,255,0.75)',
                    fontWeight: location.pathname === link.to ? 700 : 400,
                    '&.Mui-selected': { bgcolor: 'rgba(99,102,241,0.15)' },
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                  }}
                >
                  {link.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Global notification bell */}
        <NotificationBell />

        {/* Theme toggle */}
        <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
          <IconButton onClick={toggleTheme} size="small" sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
            {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        {/* Logout — hidden on mobile to save space */}
        <Button onClick={handleLogout} size="small" variant="outlined"
          sx={{ display: { xs: 'none', sm: 'flex' }, color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)',
            fontSize: '0.75rem', px: 1.5, py: 0.4, flexShrink: 0,
            '&:hover': { borderColor: 'rgba(255,255,255,0.5)', color: '#fff', backgroundColor: 'rgba(255,255,255,0.06)' } }}
        >
          Logout
        </Button>
      </Toolbar>
    </AppBar>
  );
}

function App() {
  return (
    <Router>
      <RecordingProvider>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
        <Navigation />
        <Box component="main" sx={{ flex: 1 }}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/expense/upload" element={<UploadPage />} />
            <Route path="/expense/review" element={<ReviewPage />} />
            <Route path="/expense/history" element={<HistoryPage />} />
            <Route path="/expense/dashboard" element={<DashboardPage />} />
            <Route path="/expense/trends" element={<TrendsPage />} />
            <Route path="/expense/analytics" element={<AnalyticsPage />} />
            <Route path="/workout/log" element={<WorkoutLogPage />} />
            <Route path="/workout/history" element={<WorkoutHistoryPage />} />
            <Route path="/investments/log" element={<InvestmentLogPage />} />
            <Route path="/investments/holdings" element={<InvestmentHoldingsPage />} />
            <Route path="/investments/ledger" element={<InvestmentLedgerPage />} />
            <Route path="/career" element={<CareerMapperPage />} />
            <Route path="/career/compare" element={<CareerComparisonPage />} />
            <Route path="/career/:profileId" element={<CareerMapperViewPage />} />
            <Route path="/interview" element={<InterviewPrepPage />} />
            <Route path="/interview/upload" element={<InterviewUploadPage />} />
            <Route path="/interview/review" element={<InterviewReviewPage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/journal" element={<JournalPage />} />
            <Route path="/tracker" element={<TrackerPage />} />
            <Route path="/rehearsals" element={<RehearsalsPage />} />
            {/* Legacy routes */}
            <Route path="/upload" element={<UploadPage />} />
            <Route path="/review" element={<ReviewPage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/trends" element={<TrendsPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
          </Routes>
        </Box>
      </Box>
      </RecordingProvider>
    </Router>
  );
}

export default App;
